/**
 * useAddAccountWizard — local step state for the "Add account" flow:
 * type → configure (ACT2-1031: one page). Pure state + transitions (no data persistence
 * beyond the current selection). Kept local to this feature for now; promote to a
 * shared wizard primitive if a second multi-step flow appears.
 *
 * The configure page settles the *bucket* (a `(host, db)` account identity) itself, through its
 * type-or-pick identity fields, so choosing a bucket no longer moves the wizard. We also keep a
 * representative `Warehouse` (any destination in the bucket) to drive the form's prefill.
 *
 * A hand-typed bucket has no representative, so nothing prefills the form and its fields start
 * empty. `bucket` is null until the identity fields name an account.
 */
import type { Warehouse } from '@server/types';
import { useCallback, useState } from 'react';

import type { WarehouseBucket } from '../useWarehouseAccounts';
import type { WarehouseTypeMeta } from '../warehouseTypes';
import type { SetupView } from './SnowflakeSetupStep';

export type AddAccountStep = 'type' | 'configure' | 'platform' | 'setup';

/**
 * Phase 3b: where a chosen warehouse type is created. `platform` iff the workspace creates at least
 * one of that type's families in RudderStack (`auth.me` → `platformAccountCreateFamilies`, already
 * flag ∩ fetch ramp ∩ linked).
 */
export function routeFor(type: WarehouseTypeMeta, families: readonly string[]): 'local' | 'platform' {
  return families.some((f) => f.startsWith(`${type.key}:`)) ? 'platform' : 'local';
}

/** An authentication mode of a type that keeps Lookout's local create when its family is not enabled. */
export interface LocalAlternative {
  family: string;
  label: string;
}

/**
 * LA4 / decision 9: the modes of a platform-routed type that must stay reachable through the local
 * wizard when their own family is not enabled — Redshift IAM always, Databricks OAuth when only PAT
 * is on. Both local forms really do take a credential the customer supplies.
 *
 * Snowflake offers the customer's own private key (ACT2-1031). `SNOWFLAKE:key_pair` is never created
 * on the platform (`PLATFORM_CREATE_EXCLUDED_FAMILIES`), so the local `SnowflakeForm` is its only
 * create path, and the filter below never hides it. Password stays absent: the local form has no
 * password field.
 *
 * View model only: the server re-checks every create.
 */
const LOCAL_ALTERNATIVES: Partial<Record<WarehouseTypeMeta['key'], LocalAlternative[]>> = {
  RS: [
    { family: 'RS:password', label: 'Username & password' },
    { family: 'RS:iam', label: 'IAM role' },
  ],
  SNOWFLAKE: [{ family: 'SNOWFLAKE:key_pair', label: 'Your own private key' }],
  DATABRICKS: [
    { family: 'DATABRICKS:pat', label: 'Personal access token' },
    { family: 'DATABRICKS:oauth_m2m', label: 'OAuth (machine-to-machine)' },
  ],
};

export function localAlternativesFor(type: WarehouseTypeMeta, families: readonly string[]): LocalAlternative[] {
  return (LOCAL_ALTERNATIVES[type.key] ?? []).filter((alt) => !families.includes(alt.family));
}

export function useAddAccountWizard() {
  const [step, setStep] = useState<AddAccountStep>('type');
  const [type, setType] = useState<WarehouseTypeMeta | null>(null);
  const [bucket, setBucket] = useState<WarehouseBucket | null>(null);
  /** Representative destination for the selected bucket — prefill source only. */
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  /** Phase 3b: the Snowflake setup being run (RudderStack's public key + SQL), on the `setup` step. */
  const [setup, setSetup] = useState<SetupView | null>(null);

  const reset = useCallback(() => {
    setStep('type');
    setType(null);
    setBucket(null);
    setWarehouse(null);
    setSetup(null);
  }, []);

  const selectType = useCallback((next: WarehouseTypeMeta, route: 'local' | 'platform' = 'local') => {
    setType(next);
    setBucket(null);
    setWarehouse(null);
    setStep(route === 'platform' ? 'platform' : 'configure');
  }, []);

  /** The configure page's identity fields named an account (or, with null, stopped naming one). */
  const selectBucket = useCallback((nextBucket: WarehouseBucket | null, representative: Warehouse | null) => {
    setBucket(nextBucket);
    setWarehouse(representative);
  }, []);

  const showSetup = useCallback((next: SetupView) => {
    setSetup(next);
    setStep('setup');
  }, []);

  const restartPlatform = useCallback(() => {
    setSetup(null);
    setStep('platform');
  }, []);

  const back = useCallback(() => {
    setStep((s) => (s === 'setup' ? 'platform' : 'type'));
  }, []);

  return {
    step,
    type,
    bucket,
    warehouse,
    setup,
    reset,
    selectType,
    selectBucket,
    showSetup,
    restartPlatform,
    back,
  };
}
